import { BTSelectItem } from "types/apiResponse/apiResponse";

import type { CustomFieldDefinition } from "commonComponents/entity/customField/utils/customFieldDefinition.util";

export { parseCommaSeparatedIds } from "commonComponents/entity/customField/utils/customFieldTypes/customFieldSharedUtils";

interface IHasOptions {
    options?: { id: number; text: string; sortOrder?: number }[];
}

export function getOptionsFromDefinition(definition?: CustomFieldDefinition): BTSelectItem[] {
    if (!definition) return [];
    const def = definition as IHasOptions;
    return (def.options ?? []).map(
        (opt) => new BTSelectItem({ id: opt.id, name: opt.text, value: opt.id }),
    );
}

export function getOptionText(
    selectedOptionId: number,
    definition?: CustomFieldDefinition,
): string {
    if (!definition) return String(selectedOptionId);
    const def = definition as IHasOptions;
    const option = (def.options ?? []).find((opt) => opt.id === selectedOptionId);
    return option?.text ?? String(selectedOptionId);
}

/**
 * Returns the configured display sort order for a dropdown option, falling back to
 * -1 for unknown options so they sort before any real (>= 0) sort order value.
 */
export function getOptionSortOrder(
    selectedOptionId: number,
    definition?: CustomFieldDefinition,
): number {
    if (!definition) return -1;
    const def = definition as IHasOptions;
    const option = (def.options ?? []).find((opt) => opt.id === selectedOptionId);
    return option?.sortOrder ?? -1;
}

export function getOptionTexts(ids: number[], definition?: CustomFieldDefinition): string {
    if (!definition) return ids.join(", ");
    const def = definition as IHasOptions;
    const options = def.options ?? [];
    return ids
        .map((id) => {
            const opt = options.find((o) => o.id === id);
            return opt?.text ?? String(id);
        })
        .join(", ");
}

/**
 * Returns the selected options' sort orders, ascending, for comparing multi-select values.
 * Comparing these arrays lexicographically orders rows by their lowest (first) selected
 * option's configured position, matching how the options themselves are displayed.
 */
export function getOptionSortOrders(ids: number[], definition?: CustomFieldDefinition): number[] {
    return ids.map((id) => getOptionSortOrder(id, definition)).sort((a, b) => a - b);
}
